/**
 * Session-token persistence. Two tokens live in `localStorage` under stable
 * keys so a session survives reloads: a short-lived access token (attached as
 * the `Authorization: Bearer` header by `apiClient.ts`) and a long-lived
 * refresh token (exchanged for a new pair when the access token expires). The
 * auth context writes them on login and clears them on logout; `apiClient`
 * reads them and rotates them on a 401. The keys are in `@lib` because the public
 * site, on the same origin, reads them too.
 */
import { ACCESS_TOKEN_STORAGE_KEY as ACCESS_TOKEN_KEY, REFRESH_TOKEN_STORAGE_KEY as REFRESH_TOKEN_KEY } from '@lib/constants/session-storage'

export const tokenStorage = {
  /** The stored access token, or `null` when the user is signed out. */
  get(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY)
  },

  /** The stored refresh token, or `null` when the user is signed out. */
  getRefresh(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  },

  /** Persist both tokens after a successful login or refresh. */
  setSession(accessToken: string, refreshToken: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  },

  /** Remove both tokens on logout or an unrecoverable 401. */
  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },

  /**
   * Observe session changes made by *other* tabs, with the new access token (or
   * `null` if the session was cleared). Returns an unsubscribe function.
   *
   * The `storage` event never fires in the tab that made the change, so this
   * reports only what happened elsewhere — a same-tab token rotation does not
   * come back through here.
   */
  subscribe(listener: (accessToken: string | null) => void): () => void {
    function onStorage(event: StorageEvent): void {
      // A null key means the whole store was cleared; otherwise only ours matter.
      if (event.key !== null && event.key !== ACCESS_TOKEN_KEY && event.key !== REFRESH_TOKEN_KEY) {
        return
      }
      listener(localStorage.getItem(ACCESS_TOKEN_KEY))
    }
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('storage', onStorage)
    }
  },
}
